'use client';

import Icon from '@/components/ui/AppIcon';
import { QuoteFormData } from './SmartQuoteForm';

interface MaterialSelectorProps {
  formData: QuoteFormData;
  updateFormData: (updates: Partial<QuoteFormData>) => void;
}

const materials = [
  { value: 'aluminum', label: 'Aluminum', grades: ['5052', '6061', '7075'] },
  { value: 'steel', label: 'Mild Steel', grades: ['A36', 'A572', 'A588'] },
  { value: 'stainless', label: 'Stainless Steel', grades: ['304', '316', '316L'] },
  { value: 'copper', label: 'Copper', grades: ['C110', 'C122', 'C260'] },
];

const thicknesses = ['0.5mm', '1mm', '1.5mm', '2mm', '3mm', '4mm', '5mm', '6mm'];

const finishTypes = [
  { value: 'none', label: 'No Finish', icon: 'MinusIcon' },
  { value: 'powder-coating', label: 'Powder Coating', icon: 'SparklesIcon' },
  { value: 'anodizing', label: 'Anodizing', icon: 'BeakerIcon' },
  { value: 'plating', label: 'Plating', icon: 'ShieldCheckIcon' },
];

const deliveryTimelines = [
  { value: 'standard', label: 'Standard (2-3 weeks)', icon: 'ClockIcon' },
  { value: 'expedited', label: 'Expedited (1 week)', icon: 'BoltIcon' },
  { value: 'rush', label: 'Rush (3-5 days)', icon: 'RocketLaunchIcon' },
];

export default function MaterialSelector({ formData, updateFormData }: MaterialSelectorProps) {
  const selectedMaterial = materials.find(m => m.value === formData.material);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-headline font-bold text-foreground">
          Material & Specifications
        </h2>
        <p className="text-muted-foreground font-body">
          Select your material requirements and project specifications.
        </p>
      </div>

      {/* Material Selection */}
      <div className="space-y-3">
        <label className="block text-sm font-cta font-semibold text-foreground">
          Material Type <span className="text-error">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {materials.map((material) => (
            <button
              key={material.value}
              onClick={() => updateFormData({ material: material.value, materialGrade: '' })}
              className={`p-4 rounded-lg border-2 transition-all duration-base ${
                formData.material === material.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="text-sm font-cta font-semibold text-foreground">
                {material.label}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Material Grade */}
      {selectedMaterial && (
        <div className="space-y-3">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Material Grade <span className="text-error">*</span>
          </label>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {selectedMaterial.grades.map((grade) => (
              <button
                key={grade}
                onClick={() => updateFormData({ materialGrade: grade })}
                className={`p-3 rounded-lg border-2 transition-all duration-base ${
                  formData.materialGrade === grade
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="text-sm font-cta font-semibold text-foreground">
                  {grade}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Thickness and Quantity */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Material Thickness <span className="text-error">*</span>
          </label>
          <select
            value={formData.thickness}
            onChange={(e) => updateFormData({ thickness: e.target.value })}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
          >
            <option value="">Select thickness</option>
            {thicknesses.map((thickness) => (
              <option key={thickness} value={thickness}>
                {thickness}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Quantity <span className="text-error">*</span>
          </label>
          <input
            type="number"
            value={formData.quantity}
            onChange={(e) => updateFormData({ quantity: e.target.value })}
            placeholder="Enter quantity"
            min="1"
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
          />
        </div>
      </div>

      {/* Finish Type */}
      <div className="space-y-3">
        <label className="block text-sm font-cta font-semibold text-foreground">
          Finish Type
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {finishTypes.map((finish) => (
            <button
              key={finish.value}
              onClick={() => updateFormData({ finishType: finish.value })}
              className={`p-4 rounded-lg border-2 transition-all duration-base ${
                formData.finishType === finish.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex flex-col items-center space-y-2">
                <Icon name={finish.icon as any} size={24} variant="outline" className="text-primary" />
                <div className="text-sm font-cta font-semibold text-foreground text-center">
                  {finish.label}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Delivery Timeline */}
      <div className="space-y-3">
        <label className="block text-sm font-cta font-semibold text-foreground">
          Delivery Timeline
        </label>
        <div className="grid md:grid-cols-3 gap-3">
          {deliveryTimelines.map((timeline) => (
            <button
              key={timeline.value}
              onClick={() => updateFormData({ deliveryTimeline: timeline.value })}
              className={`p-4 rounded-lg border-2 transition-all duration-base ${
                formData.deliveryTimeline === timeline.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon name={timeline.icon as any} size={24} variant="outline" className="text-primary" />
                <div className="text-sm font-cta font-semibold text-foreground text-left">
                  {timeline.label}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Additional Requirements */}
      <div className="space-y-3">
        <label className="block text-sm font-cta font-semibold text-foreground">
          Tolerance Requirements
        </label>
        <input
          type="text"
          value={formData.toleranceRequirements}
          onChange={(e) => updateFormData({ toleranceRequirements: e.target.value })}
          placeholder="e.g., ±0.1mm, ISO 2768-m"
          className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
        />
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-cta font-semibold text-foreground">
          Special Requirements
        </label>
        <textarea
          value={formData.specialRequirements}
          onChange={(e) => updateFormData({ specialRequirements: e.target.value })}
          placeholder="Any special requirements, certifications, or notes..."
          rows={4}
          className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base resize-none"
        />
      </div>
    </div>
  );
}